import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { Toaster } from '../../components/ui/sonner';
import {
  LayoutDashboard, ShoppingBag, Newspaper, Image,
  Star, Mail, Settings, Menu, X, LogOut, ChevronRight, Leaf, Eye, EyeOff, ShoppingCart
} from 'lucide-react';
import AdminDashboard from './AdminDashboard';
import AdminProdotti from './AdminProdotti';
import AdminNews from './AdminNews';
import AdminGalleria from './AdminGalleria';
import AdminRecensioni from './AdminRecensioni';
import AdminMessaggi from './AdminMessaggi';
import AdminImpostazioni from './AdminImpostazioni';
import AdminOrdini from './AdminOrdini';
import AdminOrdineDetail from './AdminOrdineDetail';
import axios from 'axios';

const ADMIN_USER = 'admin';
const ADMIN_PASS = 'Admin123';

// Sessione temporanea (tab/browser aperto)
const SESSION_KEY     = 'admin_auth';
const SESSION_EXP_KEY = 'admin_auth_exp';
const SESSION_TTL     = 8 * 60 * 60 * 1000;        // 8 ore

// Sessione persistente (PWA / "Ricordami")
const PERSIST_KEY     = 'admin_auth_persist';
const PERSIST_EXP_KEY = 'admin_auth_persist_exp';
const PERSIST_TTL     = 30 * 24 * 60 * 60 * 1000;  // 30 giorni

function clearAuth() {
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(SESSION_EXP_KEY);
  localStorage.removeItem(PERSIST_KEY);
  localStorage.removeItem(PERSIST_EXP_KEY);
}

function isSessionValid() {
  // 1. Controlla sessione persistente (localStorage — sopravvive alla chiusura PWA)
  if (localStorage.getItem(PERSIST_KEY) === '1') {
    const exp = parseInt(localStorage.getItem(PERSIST_EXP_KEY) || '0', 10);
    if (Date.now() < exp) return true;
    localStorage.removeItem(PERSIST_KEY);
    localStorage.removeItem(PERSIST_EXP_KEY);
  }
  // 2. Controlla sessione temporanea (sessionStorage — solo tab corrente)
  if (sessionStorage.getItem(SESSION_KEY) !== '1') return false;
  const exp = parseInt(sessionStorage.getItem(SESSION_EXP_KEY) || '0', 10);
  if (Date.now() > exp) { clearAuth(); return false; }
  return true;
}

// Rilevamento PWA — sincrono, valutato una volta sola
function detectPwa() {
  return (
    window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  );
}

function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  // Inizializzazione SINCRONA: se siamo in PWA parte già spuntato
  const [remember, setRemember] = useState(() => detectPwa());
  const [error, setError]       = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (username === ADMIN_USER && password === ADMIN_PASS) {
      // In PWA mode forza sempre persistente, indipendentemente dal checkbox
      const persist = remember || detectPwa();
      if (persist) {
        localStorage.setItem(PERSIST_KEY, '1');
        localStorage.setItem(PERSIST_EXP_KEY, String(Date.now() + PERSIST_TTL));
      } else {
        sessionStorage.setItem(SESSION_KEY, '1');
        sessionStorage.setItem(SESSION_EXP_KEY, String(Date.now() + SESSION_TTL));
      }
      onLogin();
    } else {
      setError('Nome utente o password non corretti.');
    }
  };

  return (
    <div className="min-h-screen bg-primary-dark flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 bg-primary-dark rounded-2xl flex items-center justify-center mb-4">
            <Leaf className="w-7 h-7 text-accent" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Accesso Admin</h1>
          <p className="font-body text-sm text-gray-500 mt-1">Le Bontà del Vesuvio</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">Nome utente</label>
            <input
              type="text"
              value={username}
              onChange={(e) => { setUsername(e.target.value); setError(''); }}
              className="w-full border border-gray-300 rounded-xl px-4 py-2.5 font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="admin"
              autoComplete="username"
            />
          </div>

          <div>
            <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">Password</label>
            <div className="relative">
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 pr-11 font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="••••••••"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Ricordami */}
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <div
              onClick={() => setRemember(r => !r)}
              className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                remember ? 'bg-primary-dark border-primary-dark' : 'bg-white border-gray-300'
              }`}
            >
              {remember && (
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <div>
              <p className="font-body text-sm text-gray-700 font-medium">Ricordami su questo dispositivo</p>
              <p className="font-body text-xs text-gray-400">Rimani connesso per 30 giorni</p>
            </div>
          </label>

          {error && (
            <p className="font-body text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">{error}</p>
          )}

          <button
            type="submit"
            className="w-full bg-primary-dark text-white font-body font-semibold py-2.5 rounded-xl hover:bg-primary transition-colors"
          >
            Accedi
          </button>
        </form>
      </div>
    </div>
  );
}

const NAV = [
  { path: '', label: 'Dashboard', icon: LayoutDashboard },
  { path: 'prodotti', label: 'Prodotti', icon: ShoppingBag },
  { path: 'ordini', label: 'Ordini', icon: ShoppingCart },
  { path: 'news', label: 'News', icon: Newspaper },
  { path: 'galleria', label: 'Galleria', icon: Image },
  { path: 'recensioni', label: 'Recensioni', icon: Star },
  { path: 'messaggi', label: 'Messaggi', icon: Mail },
  { path: 'impostazioni', label: 'Impostazioni', icon: Settings },
];

export default function AdminPage() {
  const [auth, setAuth] = useState(isSessionValid);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingOrders, setPendingOrders] = useState(0);
  const location = useLocation();

  // Sostituisce il manifest con quello admin (start_url=/admin) per il salvataggio PWA
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prev = link?.getAttribute('href') || '/manifest.json';
    if (link) link.setAttribute('href', '/manifest-admin.json');
    else {
      const el = document.createElement('link');
      el.rel = 'manifest'; el.href = '/manifest-admin.json';
      document.head.appendChild(el);
    }
    // iOS title per "Aggiungi a schermata Home"
    const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const prevTitle = appleTitle?.getAttribute('content');
    if (appleTitle) appleTitle.setAttribute('content', 'BV Admin');

    return () => {
      const l = document.querySelector('link[rel="manifest"]');
      if (l) l.setAttribute('href', prev);
      if (appleTitle && prevTitle) appleTitle.setAttribute('content', prevTitle);
    };
  }, []);

  useEffect(() => {
    if (!auth) return;
    const fetchPending = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
        const res = await axios.get(`${BACKEND_URL}/api/orders`);
        const active = res.data.filter(o => !['completed', 'cancelled'].includes(o.status));
        setPendingOrders(active.length);
      } catch { /* silenzioso */ }
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60000); // aggiorna ogni minuto
    return () => clearInterval(interval);
  }, [auth]);

  if (!auth) return <LoginPage onLogin={() => setAuth(true)} />;

  const handleLogout = () => {
    clearAuth();
    setAuth(false);
  };

  const isActive = (path) => {
    if (path === '') return location.pathname === '/admin' || location.pathname === '/admin/';
    return location.pathname.startsWith(`/admin/${path}`);
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
        <div className="w-9 h-9 bg-accent rounded-xl flex items-center justify-center flex-shrink-0">
          <Leaf className="w-5 h-5 text-primary-dark" />
        </div>
        <div>
          <p className="font-heading text-white font-bold text-sm leading-tight">Le Bont\u00e0 del</p>
          <p className="font-heading text-accent font-bold text-sm leading-tight">Vesuvio \u2014 Admin</p>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map((item) => (
          <Link
            key={item.path}
            to={`/admin${item.path ? `/${item.path}` : ''}`}
            onClick={() => setSidebarOpen(false)}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 group ${
              isActive(item.path)
                ? 'bg-accent text-primary-dark font-semibold'
                : 'text-white/70 hover:bg-white/10 hover:text-white'
            }`}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            <span className="font-body text-sm">{item.label}</span>
            <span className="ml-auto flex items-center gap-1">
              {item.path === 'ordini' && pendingOrders > 0 && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${
                  isActive(item.path) ? 'bg-primary-dark/20 text-primary-dark' : 'bg-red-500 text-white'
                }`}>
                  {pendingOrders > 99 ? '99+' : pendingOrders}
                </span>
              )}
              {isActive(item.path) && <ChevronRight className="w-4 h-4" />}
            </span>
          </Link>
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-white/10 space-y-1">
        <Link
          to="/"
          onClick={() => setSidebarOpen(false)}
          className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-white/60 hover:bg-white/10 hover:text-white transition-all"
        >
          <ChevronRight className="w-5 h-5 rotate-180" />
          <span className="font-body text-sm">Vai al sito pubblico</span>
        </Link>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-white/60 hover:bg-red-500/20 hover:text-red-300 transition-all"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-body text-sm">Esci</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex overflow-x-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-primary-dark fixed inset-y-0 left-0 z-30 shadow-xl">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-primary-dark flex flex-col shadow-2xl">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen min-w-0 overflow-x-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-8 py-4 flex items-center gap-4 sticky top-0 z-20 shadow-sm">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-bold text-gray-900">Pannello di Controllo</h1>
          </div>
          <Link
            to="/"
            className="hidden sm:flex items-center gap-2 text-sm text-gray-500 hover:text-primary transition-colors font-body px-3 py-1.5 rounded-lg hover:bg-gray-50"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Vai al sito
          </Link>
          <button
            onClick={handleLogout}
            className="hidden sm:flex items-center gap-2 text-sm text-red-500 hover:text-red-700 transition-colors font-body px-3 py-1.5 rounded-lg hover:bg-red-50"
          >
            <LogOut className="w-4 h-4" />
            Esci
          </button>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-3 sm:p-4 lg:p-8 max-w-7xl w-full mx-auto min-w-0 overflow-x-hidden">
          <Routes>
            <Route index element={<AdminDashboard />} />
            <Route path="prodotti" element={<AdminProdotti />} />
            <Route path="ordini" element={<AdminOrdini />} />
            <Route path="ordini/:id" element={<AdminOrdineDetail />} />
            <Route path="news" element={<AdminNews />} />
            <Route path="galleria" element={<AdminGalleria />} />
            <Route path="recensioni" element={<AdminRecensioni />} />
            <Route path="messaggi" element={<AdminMessaggi />} />
            <Route path="impostazioni" element={<AdminImpostazioni />} />
          </Routes>
        </main>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
}
